import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import baseURL from '../../config';

const statusColors = {
  active: { bg: '#e3fcef', color: '#097969' },
  unpaid: { bg: '#fdecea', color: '#d93025' },
  trialing: { bg: '#fff4e5', color: '#e37400' },
};

const VesselOwnerSubscriptionHistory = () => {
  const { id } = useParams();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('subscription');
  const [statusFilter, setStatusFilter] = useState('');
  const [paymentFilter, setPaymentFilter] = useState('');

  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      try {
        const response = await axios.get(
          `${baseURL}/api/vesselowner-subscription/getSubscriptionDetails/${id}`
        );
        const data = Array.isArray(response.data) ? response.data : [response.data];
        setSubscriptions(data);
      } catch (error) {
        console.error('Error fetching subscription details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionDetails();
  }, [id]);

  const active = subscriptions[0];
  const history = active?.history || [];
  const renewals = active?.renewals || [];

  if (loading) {
    return (
      <Box textAlign="center" mt={5}>
        <CircularProgress />
      </Box>
    );
  }

  if (!active) {
    return (
      <Typography align="center" mt={5}>
        No active subscription found.
      </Typography>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom color="red">
        📦 Active Subscription
      </Typography>

      <Paper
        elevation={4}
        sx={{
          p: 4,
          borderRadius: 4,
          mb: 5,
          background: 'linear-gradient(to right, #fdfbfb, #ebedee)',
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><strong>Email:</strong> {active.email}</Grid>
          <Grid item xs={12} sm={6}><strong>Billing Cycle:</strong> {active.billingCycle}</Grid>
          <Grid item xs={12} sm={6}><strong>Amount:</strong> {active.currency} {(active.priceInCents / 100).toFixed(2)}</Grid>
          <Grid item xs={12} sm={6}><strong>Vessels:</strong> {active.vesselCount}</Grid>
          <Grid item xs={12} sm={6}><strong>Start:</strong> {dayjs(active.currentPeriodStart).format('DD MMM YYYY')}</Grid>
          <Grid item xs={12} sm={6}><strong>End:</strong> {dayjs(active.currentPeriodEnd).format('DD MMM YYYY')}</Grid>
          <Grid item xs={12}>
            <strong>Status:</strong>{' '}
            <Chip
              label={active.status}
              size="small"
              sx={{
                backgroundColor: statusColors[active.status]?.bg,
                color: statusColors[active.status]?.color,
                fontWeight: 500,
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Toggle Buttons */}
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, newView) => newView && setView(newView)}
          sx={{
            borderRadius: 8,
            background: '#f1f1f1',
            boxShadow: 2,
          }}
        >
          <ToggleButton
            value="subscription"
            sx={{
              fontWeight: 'bold',
              borderRadius: 3,
              px: 3,
              '&.Mui-selected': {
                background: '#6a1b9a',
                color: 'white',
              },
            }}
          >
            📜 Subscription History
          </ToggleButton>
          <ToggleButton
            value="renewal"
            sx={{
              fontWeight: 'bold',
              borderRadius: 3,
              px: 3,
              '&.Mui-selected': {
                background: '#43a047',
                color: 'white',
              },
            }}
          >
            🔁 Renewal History
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} justifyContent="flex-end">
          {view === 'subscription' && (
            <Grid item xs={12} sm={4} md={3}>
              <Select
                fullWidth
                size="small"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value=""><em>All Status</em></MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="trialing">Trialing</MenuItem>
              </Select>
            </Grid>
          )}
          {view === 'renewal' && (
            <Grid item xs={12} sm={4} md={3}>
              <Select
                fullWidth
                size="small"
                value={paymentFilter}
                onChange={(e) => setPaymentFilter(e.target.value)}
                displayEmpty
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value=""><em>All Methods</em></MenuItem>
                <MenuItem value="wallet">Wallet</MenuItem>
                <MenuItem value="stripe">Stripe</MenuItem>
              </Select>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Table Section */}
      <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden' }}>
        <Table size="small">
          <TableHead sx={{ backgroundColor: '#f5f5f5' }}>
            <TableRow>
              {view === 'subscription' ? (
                <>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Billing</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Vessels</strong></TableCell>
                  <TableCell><strong>Updated</strong></TableCell>
                </>
              ) : (
                <>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Amount</strong></TableCell>
                  <TableCell><strong>Billing</strong></TableCell>
                  <TableCell><strong>Vessels</strong></TableCell>
                  <TableCell><strong>Payment Method</strong></TableCell>
                  <TableCell><strong>Renewed At</strong></TableCell>
                </>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {view === 'subscription' ? (
              history
                .filter((row) => !statusFilter || row.status === statusFilter)
                .map((row, idx) => (
                  <TableRow key={idx} sx={{ backgroundColor: idx % 2 ? '#fafafa' : 'white' }}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        sx={{
                          backgroundColor: statusColors[row.status]?.bg,
                          color: statusColors[row.status]?.color,
                          fontWeight: 500,
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.billingCycle}</TableCell>
                    <TableCell>{active.currency} {(row.priceInCents / 100).toFixed(2)}</TableCell>
                    <TableCell>{row.vesselCount}</TableCell>
                    <TableCell>{dayjs(row.updatedAt).format('DD MMM YYYY, hh:mm A')}</TableCell>
                  </TableRow>
                ))
            ) : (
              renewals
                .filter((item) => !paymentFilter || item.paymentMethod === paymentFilter)
                .map((item, idx) => (
                  <TableRow key={idx} sx={{ backgroundColor: idx % 2 ? '#fafafa' : 'white' }}>
                    <TableCell>{idx + 1}</TableCell>{console.log("item", item)}
                    <TableCell>{item.currency} {item.amount}</TableCell>
                    <TableCell>{item.billingCycle || 'N/A'}</TableCell>
                    <TableCell>{item.vesselCount || 'N/A'}</TableCell>
                    <TableCell>{item.paymentMethod || 'stripe'}</TableCell>
                    <TableCell>{dayjs(item.renewedAt).format('DD MMM YYYY, hh:mm A')}</TableCell>
                  </TableRow>
                ))
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

export default VesselOwnerSubscriptionHistory;
